import type { StaffRole } from '@iris/shared';
import { BrandMark } from '@iris/ui/components/brand-mark';
import { cn } from '@iris/ui/lib/utils';
import {
  CalendarClock,
  CreditCard,
  Gift,
  LayoutDashboard,
  type LucideIcon,
  Megaphone,
  MoreHorizontal,
  ScanLine,
  Settings,
  ShieldAlert,
  Sparkles,
  Store,
  UserRound,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';

import { useOpenAlerts } from './alerts/alerts-kit';
import { useShops } from './shop-context';

interface Section {
  to: string;
  label: string;
  icon: LucideIcon;
  roles: StaffRole[];
  /** ADR 0012 D4: the desktop sidebar groups its screens */
  group: 'activity' | 'loyalty' | 'manage';
}

/** Every screen of owner and manager (T4 §1.2), in sidebar order. */
export const SECTIONS: Section[] = [
  {
    to: '/dashboard',
    label: 'dashboard.title',
    icon: LayoutDashboard,
    roles: ['OWNER', 'MANAGER'],
    group: 'activity',
  },
  { to: '/scan', label: 'counter.title', icon: ScanLine, roles: ['OWNER', 'MANAGER'], group: 'activity' },
  { to: '/today', label: 'today.title', icon: CalendarClock, roles: ['OWNER', 'MANAGER'], group: 'activity' },
  {
    to: '/promotions',
    label: 'promotions.title',
    icon: Megaphone,
    roles: ['OWNER', 'MANAGER'],
    group: 'loyalty',
  },
  { to: '/programs', label: 'programs.link', icon: Sparkles, roles: ['OWNER', 'MANAGER'], group: 'loyalty' },
  { to: '/rewards', label: 'rewards.link', icon: Gift, roles: ['OWNER', 'MANAGER'], group: 'loyalty' },
  { to: '/alerts', label: 'alerts.title', icon: ShieldAlert, roles: ['OWNER', 'MANAGER'], group: 'manage' },
  { to: '/staff', label: 'staff.link', icon: Users, roles: ['OWNER', 'MANAGER'], group: 'manage' },
  { to: '/shop', label: 'shopSettings.title', icon: Store, roles: ['OWNER', 'MANAGER'], group: 'manage' },
  { to: '/billing', label: 'billing.title', icon: CreditCard, roles: ['OWNER', 'MANAGER'], group: 'manage' },
  { to: '/settings', label: 'settings.title', icon: Settings, roles: ['OWNER'], group: 'manage' },
  { to: '/account', label: 'profile.title', icon: UserRound, roles: ['OWNER', 'MANAGER'], group: 'manage' },
];

/** What the phone bar shows (Scanner · Tableau de bord · Promos · Plus); the rest is under "Plus". */
const BAR = ['/scan', '/dashboard', '/promotions'];

export function sectionsFor(role: StaffRole): Section[] {
  return SECTIONS.filter((section) => section.roles.includes(role));
}

/**
 * Desktop (≥ 1024 px, ADR 0012 D4): a fixed sidebar (left, right in Arabic) — the brand, the shop, every screen
 * grouped (activity, loyalty, management) and, at the bottom, the language and the account.
 */
export function StaffSidebar({ role, footer }: { role: StaffRole; footer: ReactNode }) {
  const { t } = useTranslation();
  const open = useOpenAlerts();
  const { current } = useShops();
  const groups = (['activity', 'loyalty', 'manage'] as const).map((group) => ({
    group,
    items: sectionsFor(role).filter((section) => section.group === group),
  }));
  return (
    <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-e border-border/60 bg-card lg:flex print:hidden">
      <div className="px-5 pb-3 pt-6">
        <Link to="/" className="flex items-center gap-2.5 text-xl font-semibold tracking-tight text-primary">
          <BrandMark />
          {t('brand.name')}
        </Link>
      </div>
      {current ? (
        <div className="mx-3 mb-2 flex items-center gap-3 rounded-xl bg-muted/60 px-3 py-2.5">
          {current.logoUrl ? (
            <img
              src={current.logoUrl}
              alt=""
              className="h-9 w-9 shrink-0 rounded-lg object-cover shadow-sm"
            />
          ) : (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Store aria-hidden className="h-4 w-4" />
            </span>
          )}
          <span className="min-w-0">
            <bdi className="block truncate text-sm font-semibold">{current.displayName}</bdi>
            <span className="block text-xs text-muted-foreground">{t(`roles.${current.role}`)}</span>
          </span>
        </div>
      ) : null}
      <nav aria-label={t('nav.label')} className="flex-1 space-y-5 overflow-y-auto px-3 py-3">
        {groups.map(({ group, items }) => (
          <div key={group} className="space-y-1">
            <p className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">
              {t(`nav.groups.${group}`)}
            </p>
            <ul className="space-y-0.5">
              {items.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    className={({ isActive }) =>
                      cn(
                        'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-150',
                        isActive
                          ? 'bg-primary/10 font-semibold text-primary before:absolute before:inset-y-1.5 before:start-0 before:w-1 before:rounded-full before:bg-primary'
                          : 'text-foreground/80 hover:bg-muted hover:text-foreground',
                      )
                    }
                  >
                    <Icon
                      aria-hidden
                      className="h-4 w-4 shrink-0 transition-transform duration-150 group-hover:scale-110"
                    />
                    <span className="flex-1">{t(label)}</span>
                    {to === '/alerts' && open > 0 ? <Count value={open} /> : null}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="border-t border-border/60 p-3">{footer}</div>
    </aside>
  );
}

/** Phone and tablet: the bottom bar. */
export function StaffTabs() {
  const { t } = useTranslation();
  const open = useOpenAlerts();
  const tab = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex flex-1 flex-col items-center gap-1 py-2 text-xs',
      isActive ? 'font-semibold text-primary' : 'text-muted-foreground',
    );
  const items = SECTIONS.filter((section) => BAR.includes(section.to)).sort(
    (a, b) => BAR.indexOf(a.to) - BAR.indexOf(b.to),
  );
  return (
    <nav
      aria-label={t('nav.label')}
      className="fixed inset-x-0 bottom-0 z-40 border-t bg-background pb-[env(safe-area-inset-bottom)] lg:hidden print:hidden"
    >
      <div className="mx-auto flex max-w-lg">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={tab}>
            <Icon aria-hidden className="h-5 w-5" />
            {to === '/promotions' ? t('nav.promos') : t(label)}
          </NavLink>
        ))}
        <NavLink to="/more" className={tab}>
          <span className="relative">
            <MoreHorizontal aria-hidden className="h-5 w-5" />
            {open > 0 ? (
              <span className="absolute -top-1.5 start-3">
                <Count value={open} />
              </span>
            ) : null}
          </span>
          {t('nav.more')}
        </NavLink>
      </div>
    </nav>
  );
}

/** The open-alerts badge (owner decision M25). */
function Count({ value }: { value: number }) {
  const { t } = useTranslation();
  return (
    <span
      aria-label={t('alerts.badge', { count: value })}
      className="min-w-4 rounded-full bg-destructive px-1 text-center text-[10px] font-semibold leading-4 text-destructive-foreground"
    >
      <bdi dir="ltr">{value > 9 ? '9+' : value}</bdi>
    </span>
  );
}
